<script lang="ts">
import { defineComponent, h, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, onUpdated, ref, Teleport } from 'vue'
import { Pagination } from 'ant-design-vue'

// 2026-10-06：管理端分页按剩余空间同行，独立显示下拉框，并在所属滚动区域内保持可见。
export default defineComponent({
  name: 'APagination',
  inheritAttrs: false,
  setup(_, { attrs, slots }) {
    const anchor = ref<HTMLElement>()
    const panel = ref<HTMLElement>()
    const ready = ref(false)
    let frame = 0
    let active = true
    let disposed = false
    let observer: ResizeObserver | undefined
    let mutations: MutationObserver | undefined
    let host: HTMLElement | undefined
    let actionRow: HTMLElement | undefined
    let inline = false
    const visible = (el: HTMLElement) => el.isConnected && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'

    function layout() {
      frame = 0
      const source = anchor.value, floating = panel.value
      if (!source || !floating) return
      const scope = source.closest<HTMLElement>('.ant-modal-body, .ant-modal-footer, .ant-drawer-body, .ant-drawer-footer, .c-modal-body, .c-modal-footer, #appmain')
      if (!active || !scope || !visible(source) || !visible(scope)) {
        floating.style.display = 'none'
        return
      }
      // 弹窗叠加时隐藏底层分页，关闭上层弹窗后恢复，避免独立浮层穿透遮罩。
      const drawer = source.closest('.ant-drawer')
      if (drawer && !drawer.classList.contains('ant-drawer-open')) { floating.style.display = 'none'; return }
      const modalSelector = '.ant-modal-wrap, .ant-drawer-open > .ant-drawer-content-wrapper, .c-modal-cover'
      const modals = Array.from(document.querySelectorAll<HTMLElement>(modalSelector)).filter(visible)
      const owner = source.closest(modalSelector)
      const topModal = modals.sort((a, b) => (Number(getComputedStyle(a).zIndex) || 1000) - (Number(getComputedStyle(b).zIndex) || 1000)).pop()
      if (topModal && topModal !== owner) { floating.style.display = 'none'; return }
      floating.style.display = 'block'
      floating.style.maxWidth = 'none'
      const naturalWidth = floating.getBoundingClientRect().width
      if (actionRow) source.style.flexBasis = `${naturalWidth}px`
      const viewport = scope.getBoundingClientRect()
      const bounds = source.getBoundingClientRect()
      const left = Math.max(8, bounds.left, viewport.left)
      const right = Math.min(window.innerWidth - 8, bounds.right, viewport.right)
      const available = Math.max(0, right - left)
      if (!available) { floating.style.display = 'none'; return }
      floating.style.maxWidth = `${available}px`
      const pager = floating.querySelector<HTMLElement>('.ant-pagination')
      if (!pager) { floating.style.display = 'none'; source.style.height = '0px'; return }
      // 测量完整分页的自然宽度；窄窗口允许分页自身换行，避免两端被横向裁掉。
      const width = Math.min(naturalWidth, available)
      const height = floating.getBoundingClientRect().height
      let top = bounds.top
      let nextInline = false
      const pureHost = host && host.children.length === 1
      if (pureHost) {
        const rows = Array.from(scope.querySelectorAll<HTMLElement>('.admin-list-toolbar, .query-command, .query, .guide-toolbar, .pagination-actions'))
        for (const row of rows.reverse()) {
          if (!visible(row) || row.contains(source)) continue
          const rect = row.getBoundingClientRect()
          const before = Boolean(row.compareDocumentPosition(source) & Node.DOCUMENT_POSITION_FOLLOWING)
          const distance = before ? bounds.top - rect.bottom : rect.top - bounds.bottom
          if (distance < -4 || distance > 48 || rect.right < right - 4 || rect.left > left + 4) continue
          if (row.scrollWidth > row.clientWidth + 1) continue
          const controls = Array.from(row.querySelectorAll<HTMLElement>('input, button, label, .ant-select, .ant-picker, .ant-tabs-nav-list, .ant-tag, .ant-switch'))
          const occupied = controls.filter(visible).map(el => el.getBoundingClientRect().right)
          if (!occupied.length) continue
          // 留出完整分页宽度后才同行，不压缩或遮挡已有筛选和按钮。
          if (right - Math.max(...occupied) >= width + 16 && rect.height >= height - 2) {
            top = rect.top + (rect.height - height) / 2
            nextInline = true
          }
          break
        }
      }
      if (inline !== nextInline) {
        inline = nextInline
        host?.classList.toggle('admin-pagination-inline-host', inline)
      }
      source.style.height = inline ? '0px' : `${height}px`
      const minimum = Math.max(8, viewport.top + 4)
      const maximum = Math.min(window.innerHeight - 8, viewport.bottom - 4) - height
      floating.style.left = `${Math.max(left, right - width)}px`
      floating.style.top = `${Math.max(minimum, Math.min(top, maximum))}px`
      floating.style.visibility = 'visible'
      floating.style.zIndex = scope.id === 'appmain' ? '20' : '1050'
      floating.dataset.inline = String(inline)
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(layout)
    }
    function onScroll(event: Event) {
      // 下拉列表内部的滚动不触发布局，防止选项列表跳动。
      if (event.target instanceof Node && panel.value?.contains(event.target)) return
      schedule()
    }
    function containWheel(event: WheelEvent) {
      const target = event.target as HTMLElement
      const dropdown = target.closest('.ant-select-dropdown')
      if (!dropdown) return
      event.stopPropagation()
      const list = dropdown.querySelector<HTMLElement>('.rc-virtual-list-holder')
      if (!list || (event.deltaY < 0 && list.scrollTop <= 0) || (event.deltaY > 0 && list.scrollTop + list.clientHeight >= list.scrollHeight - 1)) event.preventDefault()
    }
    onMounted(async () => {
      const parent = anchor.value?.parentElement
      if (parent?.matches('.admin-list-pagination, .pagination, .invoice-pagination')) {
        host = parent
        host.classList.add('admin-pagination-host')
      }
      if (parent && Array.from(parent.children).some(el => el.matches('.ant-btn'))) {
        actionRow = parent
        actionRow.classList.add('admin-pagination-action-row')
      }
      ready.value = true
      await nextTick()
      if (disposed) return
      observer = new ResizeObserver(schedule)
      if (anchor.value) {
        let current: HTMLElement | null = anchor.value
        while (current && current !== document.body) { observer.observe(current); current = current.parentElement }
      }
      if (panel.value) {
        observer.observe(panel.value)
        panel.value.addEventListener('wheel', containWheel, { passive: false })
      }
      window.addEventListener('resize', schedule)
      document.addEventListener('scroll', onScroll, true)
      mutations = new MutationObserver(records => {
        if (records.some(record => record.target instanceof HTMLElement && !record.target.closest('.admin-pagination-panel, .admin-pagination-anchor'))) schedule()
      })
      mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] })
      schedule()
    })
    onUpdated(schedule)
    onActivated(() => { active = true; schedule() })
    onDeactivated(() => { active = false; schedule() })
    onBeforeUnmount(() => {
      active = false
      disposed = true
      cancelAnimationFrame(frame)
      observer?.disconnect()
      mutations?.disconnect()
      window.removeEventListener('resize', schedule)
      document.removeEventListener('scroll', onScroll, true)
      panel.value?.removeEventListener('wheel', containWheel)
      host?.classList.remove('admin-pagination-host', 'admin-pagination-inline-host')
      actionRow?.classList.remove('admin-pagination-action-row')
    })
    return () => h('div', { ref: anchor, class: 'admin-pagination-anchor' }, ready.value ? [
      h(Teleport, { to: 'body' }, h('div', { ref: panel, class: 'admin-pagination-panel', style: { display: 'none' } }, [h(Pagination, attrs, slots)]))
    ] : [])
  }
})
</script>

<style lang="scss">
.admin-pagination-anchor { width: 100%; min-width: 0; flex: 1 1 auto; }
.admin-pagination-action-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  > .admin-pagination-anchor { width: auto; flex: 1 0 auto; max-width: 100%; }
  > .ant-btn { flex: 0 0 auto; }
}
.admin-pagination-panel {
  position: fixed;
  width: max-content;
  padding: 2px 0;
  background: #fff;
  box-sizing: border-box;
  .ant-pagination { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; row-gap: 4px; margin: 0; }
  .ant-pagination > li { flex: 0 0 auto; }
  .ant-pagination-options { display: flex; align-items: center; flex-wrap: wrap; row-gap: 4px; }
  .ant-pagination-options-quick-jumper { white-space: nowrap; }
  .ant-select-dropdown .rc-virtual-list-holder { overscroll-behavior: contain; }
}
// 2026-10-06：分页容器不再横滚；仅空出的纯分页行折叠，不影响同一行其他操作。
#appmain .admin-pagination-host, .ant-modal .admin-pagination-host, .ant-drawer .admin-pagination-host {
  overflow: visible;
  min-width: 0;
  max-width: 100%;
  .admin-pagination-anchor { min-width: 0; }
  &.admin-pagination-inline-host { height: 0; min-height: 0; margin-top: 0; margin-bottom: 0; padding-top: 0; padding-bottom: 0; }
}
</style>
